import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { AuthUser } from '@edge-vue/shared/types'
import { useApi } from '@/composables/useApi'
import { tarslogs, tarslogsEnabled } from '@/plugins/tarslogs'

export const useAuthStore = defineStore('auth', () => {
  const api = useApi()

  // State
  const user = ref<AuthUser | null>(null)
  const initialized = ref(false)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const mfaToken = ref<string | null>(null)
  const forceMfa = ref(false)

  // Getters
  const isAuthenticated = computed(() => !!user.value)
  const mfaEnabled = computed(() => user.value?.mfaEnabled ?? false)

  // Actions
  async function fetchConfig() {
    try {
      const data = await api.get<{ forceMfa: boolean; betaMode: boolean }>('/auth/config')
      forceMfa.value = data.forceMfa
    } catch {
      // Config fetch is non-critical
    }
  }

  async function checkAuth() {
    if (initialized.value) return

    try {
      loading.value = true
      const data = await api.get<{ user: AuthUser | null }>('/auth/me')
      user.value = data.user
      if (tarslogsEnabled && data.user) {
        tarslogs.setContext({ userId: data.user.id, email: data.user.email })
      }
    } catch {
      user.value = null
    } finally {
      loading.value = false
      initialized.value = true
    }
  }

  async function login(email: string, password: string): Promise<boolean | 'mfa-required'> {
    try {
      loading.value = true
      error.value = null
      const data = await api.post<{ user?: AuthUser; mfaRequired?: boolean; mfaToken?: string }>(
        '/auth/login',
        { email, password }
      )

      if (data.mfaRequired && data.mfaToken) {
        mfaToken.value = data.mfaToken
        return 'mfa-required'
      }

      if (data.user) {
        user.value = data.user
        if (tarslogsEnabled) {
          tarslogs.setContext({ userId: data.user.id, email: data.user.email })
        }
        return true
      }

      return false
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Login failed'
      return false
    } finally {
      loading.value = false
    }
  }

  async function completeMfaLogin(code: string): Promise<boolean> {
    if (!mfaToken.value) return false

    try {
      loading.value = true
      error.value = null
      const data = await api.post<{ user: AuthUser }>('/auth/login/mfa', {
        mfaToken: mfaToken.value,
        code
      })
      user.value = data.user
      mfaToken.value = null
      if (tarslogsEnabled) {
        tarslogs.setContext({ userId: data.user.id, email: data.user.email })
      }
      return true
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'MFA verification failed'
      return false
    } finally {
      loading.value = false
    }
  }

  async function register(
    email: string,
    password: string,
    name?: string,
    mfaSecret?: string,
    mfaCode?: string
  ) {
    try {
      loading.value = true
      error.value = null
      const body: Record<string, string | undefined> = { email, password, name }
      if (mfaSecret) body.mfaSecret = mfaSecret
      if (mfaCode) body.mfaCode = mfaCode

      const data = await api.post<{ user: AuthUser }>('/auth/register', body)
      user.value = data.user
      if (tarslogsEnabled) {
        tarslogs.setContext({ userId: data.user.id, email: data.user.email })
      }
      return true
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Registration failed'
      return false
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    try {
      await api.post('/auth/logout', {})
    } finally {
      user.value = null
      mfaToken.value = null
      if (tarslogsEnabled) {
        tarslogs.clearContext()
      }
    }
  }

  function clearError() {
    error.value = null
  }

  return {
    // State
    user,
    initialized,
    loading,
    error,
    mfaToken,
    forceMfa,
    // Getters
    isAuthenticated,
    mfaEnabled,
    // Actions
    fetchConfig,
    checkAuth,
    login,
    completeMfaLogin,
    register,
    logout,
    clearError
  }
})
